import React, { useEffect, useRef, useState } from 'react';
import { TESTIMONIALS } from '../../data/credibility.mjs';
import './credibility.css';

/**
 * Slider de testimonios: cita + nombre + cargo + empresa + logo (+ video opcional).
 * Autoplay cada 7 s, pausa en hover/focus, respeta prefers-reduced-motion.
 * Renderiza null si no hay testimonios.
 */
const Testimonials = ({ items = TESTIMONIALS, title = 'Lo que dicen nuestros clientes', interval = 7000 }) => {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const reduced = useRef(false);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        }
    }, []);

    useEffect(() => {
        if (items.length < 2 || paused || reduced.current) return undefined;
        const id = setInterval(() => setIndex((i) => (i + 1) % items.length), interval);
        return () => clearInterval(id);
    }, [items.length, paused, interval]);

    if (!items.length) return null;
    const t = items[Math.min(index, items.length - 1)];

    return (
        <section
            className="testimonials"
            aria-label="Testimonios de clientes"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            <div className="container">
                <h2 className="testimonials__title" data-reveal="up">{title}</h2>
                <figure className="testimonials__card" aria-live="polite" data-reveal="zoom" data-reveal-delay="1">
                    <div className="testimonials__quote-icon" aria-hidden="true">
                        <i className="fas fa-quote-left"></i>
                    </div>
                    <blockquote className="testimonials__quote mb-0">
                        <p className="mb-0">“{t.quote}”</p>
                    </blockquote>
                    <figcaption className="testimonials__author mt-4">
                        {t.logo && <img src={t.logo} alt={t.company} loading="lazy" />}
                        <div>
                            <span className="testimonials__name">{t.author}</span>
                            <span className="testimonials__role">
                                {t.role}
                                {t.company ? `, ${t.company}` : ''}
                            </span>
                        </div>
                    </figcaption>
                    {t.videoUrl && (
                        <a className="testimonials__video" href={t.videoUrl} target="_blank" rel="noopener noreferrer">
                            <i className="fas fa-play-circle" aria-hidden="true"></i> Ver testimonio en video
                        </a>
                    )}
                </figure>
                {items.length > 1 && (
                    <div className="testimonials__controls" role="tablist" aria-label="Seleccionar testimonio">
                        {items.map((it, i) => (
                            <button
                                key={it.author + i}
                                type="button"
                                className="testimonials__dot"
                                aria-label={`Testimonio ${i + 1} de ${items.length}`}
                                aria-current={i === index ? 'true' : undefined}
                                onClick={() => setIndex(i)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default Testimonials;
